<!-- src/views/management/sites.vue -->
<script setup lang="ts">
import ConfirmDialogCustom from '@/components/dialogs/ConfirmDialogCustom.vue'
import LogoutTime from '@/layouts/components/LogoutTime.vue'
import UserProfile from '@/layouts/components/UserProfile.vue'

import { type as siteTypeList } from '@/datas/sites'
import { getProjectId } from '@/utils/cdms'
import { useSnackBar } from '@/utils/stores/snackbar'
import { onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { VDataTable } from 'vuetify/labs/VDataTable'

definePage({
  meta: { layout: 'blank', action: 'read', subject: 'sites' },
})

const snackbarStore = useSnackBar()

/* ── 상단 '관리' 탭 라우팅 동기화 ───────────────────────── */
type MgmtTab = 'permissions' | 'users' | 'sites'
const mgmtTab = ref<MgmtTab>('sites')
const route = useRoute()
const router = useRouter()

function syncTabFromRoute() {
  const p = route.path || ''
  if (p.includes('/manage/permissions')) mgmtTab.value = 'permissions'
  else if (p.includes('/manage/users'))  mgmtTab.value = 'users'
  else                                       mgmtTab.value = 'sites'
}
onMounted(syncTabFromRoute)
watch(() => route.fullPath, syncTabFromRoute)
watch(mgmtTab, v => {
  const next =
    v === 'permissions' ? '/manage/permissions'
    : v === 'users'     ? '/manage/users'
    : '/manage/sites'
  if (route.path !== next) router.push(next)
})

/* ── 상단 헤더 hover ───────────────────────────────────── */
const hover = ref(false)

/* ── 리스트 & 테이블 상태 (편집에서 복귀 시에만 복원) ──── */
const STORAGE_KEY = 'manage-sites-list-state'
const RETURN_FLAG = 'manage-sites-returning'
const isReturning = sessionStorage.getItem(RETURN_FLAG) === '1'
const savedState = isReturning ? JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}') : {}
sessionStorage.removeItem(RETURN_FLAG)
sessionStorage.removeItem(STORAGE_KEY)

const search = ref(savedState.search || '')
const page = ref(savedState.page || 1)
const itemsPerPage = ref(savedState.itemsPerPage || 10)

const headers = [
  { title: '기관',       key: 'name',  width: '30%' },
  { title: '연락처',     key: 'phone' },
  { title: '대표자',     key: 'investigator' },
  { title: '유형',       key: 'type' },
  { title: '편집',       key: 'actions', sortable: false, width: '160px' },
]

const typeData = siteTypeList

const typeText = (val: string | number) => {
  const found = typeData.find(({ value }: any) => String(value) === String(val))
  return found ? found.text : '-'
}

/* ── 목록 로드 ─────────────────────────────────────────── */
const { data: serverItems, execute: fetchSites } =
  await useApi(createUrl('/management/sites', {
    query: { projectId: getProjectId() },
  }))

const loadItems = () => { fetchSites() }

const updateOptions = (options: any) => { page.value = options.page }

/* ── 검색 필터 ─────────────────────────────────────────── */
const filterItems = [
  { value: 'name',         title: '기관' },
  { value: 'phone',        title: '연락처' },
  { value: 'investigator', title: '대표자' },
]
const filterItem = ref<string>(savedState.filterItem || 'name')

// 편집 페이지로 이동할 때 현재 상태 저장
const goToEdit = (id: number) => {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
    page: page.value,
    search: search.value,
    filterItem: filterItem.value,
    itemsPerPage: itemsPerPage.value,
  }))
  router.push({ name: 'manage-sites-edit-id', params: { id } })
}

const filterText = (value: any, query: string, item: any) => {
  return value != null && query != null &&
    typeof value === 'string' &&
    String(item.columns[filterItem.value] || '')
      .toLowerCase()
      .includes(query.toLowerCase())
}

/* ── 삭제/복원 ────────────────────────────────────────── */
const deleteItem = async (item: any, deleted: 0 | 1 = 1) => {
  await $api(`/management/sites/${item.id}`, { method: 'DELETE', body: { deleted } })
  snackbarStore.showSnackBar(deleted === 0 ? '복원되었습니다.' : '삭제되었습니다.')
  loadItems()
}

/* ── 완전삭제 ─────────────────────────────────────────── */
const forceDeleteConfirm = ref(false)
const forceDeleteTargetId = ref<number | null>(null)

const openForceDelete = (id: number) => {
  forceDeleteTargetId.value = id
  forceDeleteConfirm.value = true
}

const onForceDeleteConfirm = async (confirmed: boolean) => {
  if (!confirmed) {
    forceDeleteTargetId.value = null
    return
  }
  await $api(`/management/sites/${forceDeleteTargetId.value}/force`, { method: 'DELETE' })
  snackbarStore.showSnackBar('완전삭제 되었습니다.')
  forceDeleteTargetId.value = null
  loadItems()
}
</script>

<template>
  <!-- 상단 헤더 -->
  <VCard class="topbar-card" style="border-radius:0;">
  <VCardText class="d-flex justify-space-between align-between py-2">
    <!-- 👇 로고 버튼 (왼쪽) -->
    <div
      class="d-flex align-center cursor-pointer"
      @click="router.push('/projects')"
    >
      <img
        src="/images/home_logo.png"
        alt="홈"
        width="87"
        height="30"
        style="display:block"
      />
    </div>

    <!-- 오른쪽 기존 요소 -->
    <div class="d-flex align-center">
      <LogoutTime class="me-2" />
      <UserProfile />
      <div class="divider-vert" />
      <div
        class="mgmt-quick d-flex align-center px-2 py-1"
        :style="{ backgroundColor: hover ? 'rgba(0,0,0,0.05)' : 'transparent' }"
        @click="router.push('/manage/permissions')"
        @mouseover="hover = true"
        @mouseleave="hover = false"
      >
        <img src="/images/settings.png" alt="관리" class="icon-20 me-1" />
        <span class="text-gray-600">관리</span>
      </div>
    </div>
  </VCardText>
</VCard>

  <!-- 페이지 타이틀 -->
  <div class="page-title-wrap">
    <h2 class="page-title">관리</h2>
  </div>

  <!-- 관리 탭 (권한/사용자 화면과 동일 룩) -->
  <div class="mgmt-tabs">
    <VBtnToggle v-model="mgmtTab" class="tab-toggle" mandatory divided borderless>
      <VBtn :value="'permissions'" class="tab-btn">권한 관리</VBtn>
      <VBtn :value="'users'"       class="tab-btn">전체 사용자 관리</VBtn>
      <VBtn :value="'sites'"       class="tab-btn">전체 기관 관리</VBtn>
    </VBtnToggle>
  </div>

  <!-- 본문 -->
  <VRow class="page-wrap">
    <VCol cols="12">
      <VCard elevation="1">
        <!-- 상단 툴바 -->
        <VCardText class="toolbar">
          <div class="spacer" />
          <div class="d-flex flex-row align-center gap-2">
            <AppSelect v-model="filterItem" :items="filterItems" style="inline-size:150px;" />
            <AppTextField
              v-model="search"
              class="pe-2"
              style="max-inline-size:220px; min-inline-size:220px;"
              :placeholder="$t('Search') + '...'"
            />
            <VBtn
              prepend-icon="tabler-plus"
              color="#3857F5"
              class="custom-btn-36"
              @click="router.push('/manage/sites/create')"
            >
              기관 등록
            </VBtn>
          </div>
        </VCardText>

        <!-- 데이터 테이블 -->
        <VCardText class="pt-0">
          <VDataTable

            class="custom-table"
            v-model:items-per-page="itemsPerPage"
            :page="page"
            :items="serverItems"
            :headers="headers"
            :search="search"
            :custom-filter="filterText"
            density="compact"
            @update:options="updateOptions"
          >
            <template #item.name="{ item }">
              <span :class="{ deleted: item.deleted != '0' }">{{ item.name }}</span>
            </template>
            <template #item.phone="{ item }">
              <span :class="{ deleted: item.deleted != '0' }">{{ item.phone }}</span>
            </template>
            <template #item.investigator="{ item }">
              <span :class="{ deleted: item.deleted != '0' }">{{ item.investigator }}</span>
            </template>
            <template #item.type="{ item }">
              <VChip class="font-weight-medium" size="small" :color="item.deleted != '0' ? 'grey' : 'primary'">
                {{ typeText(item.type) }}
              </VChip>
            </template>

            <template #item.actions="{ item }">
              <div class="d-flex gap-1">
                <IconBtn @click="goToEdit(item.id)">
                  <VIcon icon="tabler-edit" />
                  <VTooltip activator="parent" location="top">수정</VTooltip>
                </IconBtn>
                <!-- 삭제 (1차) -->
                <IconBtn v-if="item.deleted != '1'" @click="deleteItem(item, 1)">
                  <VIcon icon="tabler-trash" color="error" />
                  <VTooltip activator="parent" location="top">삭제</VTooltip>
                </IconBtn>
                <!-- 복원 -->
                <IconBtn v-if="item.deleted == '1'" @click="deleteItem(item, 0)">
                  <VIcon icon="tabler-restore" color="success" />
                  <VTooltip activator="parent" location="top">복원</VTooltip>
                </IconBtn>
                <!-- 완전삭제 -->
                <IconBtn v-if="item.deleted == '1'" @click="openForceDelete(item.id)">
                  <VIcon icon="tabler-trash-x" color="error" />
                  <VTooltip activator="parent" location="top">완전삭제</VTooltip>
                </IconBtn>
              </div>
            </template>
          </VDataTable>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>

  <ConfirmDialogCustom
    v-model="forceDeleteConfirm"
    confirmation-question="완전삭제하면 복원할 수 없습니다.<br/>정말 삭제하시겠습니까?"
    confirm-msg=""
    cancel-msg=""
    confirm-color="error"
    @confirm="onForceDeleteConfirm"
  />
</template>

<style scoped>
/* ── 헤더 공통 ── */
.divider-vert{ background-color:rgba(0,0,0,10%); block-size:30px; inline-size:1px; margin-inline:15px; }
.icon-20{ block-size:20px; inline-size:20px; }
.text-gray-600{ color:#4B5563; font-size:14px; }
.mgmt-quick{ border-radius:6px; cursor:pointer; transition:background-color .2s; }

/* ── 탭 공통 ── */
.mgmt-tabs{ display:flex; justify-content:flex-start; padding-block:16px 0; padding-inline:34px; }
.tab-toggle{ border:1px solid #E8ECEF; border-radius:8px; background:#f4f6fa; box-shadow:0 3px 8px rgba(201,201,201,25%); }
.tab-btn{ min-inline-size:160px; }
.tab-btn.v-btn--active{ background:#eef0ff !important; }


/* ── 페이지 타이틀 ─────────────────────────────────────── */
.page-title-wrap{ padding-block: 50px 0; padding-inline: 34px; }

.page-title{ margin:0;
  border-block-end: 1px solid #DAE6EE; color:#111827; font-size:22px; font-weight:700;
  margin-block-end : 20px;
  padding-block-end: 20px; }


/* ── 본문 ── */
.page-wrap{ margin-block:14px 28px; margin-inline:34px; }
.toolbar{ display:flex; align-items:center; justify-content:space-between; }
.spacer{ flex:1; }
.custom-btn-36{ block-size:36px !important; min-block-size:36px !important; }
.deleted{ text-decoration: line-through; }


.v-data-table .v-table__wrapper > table > thead > tr > th,
.v-data-table .v-table__wrapper > table > tbody > tr > th {
  font-size: 15px;
}



:deep(.custom-table .v-table__wrapper table) {
  th {
    background: #F3F5F8 !important;  /* 밝은 회색 배경 */
    border-block-end: 1px solid #eee;
    color: #909090 !important;       /* 연한 회색 텍스트 */
    font-size: 13px;
    font-weight: 500;
    padding-block: 10px;
    padding-inline: 12px;
  }

  td {
    border-block-end: 1px solid #E8EBEE;
    color: #444;
    font-size: 14px;
    padding-block: 8px;
    padding-inline: 12px;
  }

  tr:hover td {
    background-color: #f9f9fc; /* hover 시 은은한 하이라이트 */
  }
}

:deep(.custom-table .v-badge__badge) {
  border-radius: unset;
  font-size: 8px;
  margin-inline-start: 5px;
  min-inline-size: 10px;
}

:deep(.v-card-item) {
  padding: 12px;
}
</style>
